import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageShell from '../Components/ui/PageShell';
import DataTable from '../Components/ui/DataTable';
import StatusBadge from '../Components/ui/StatusBadge';
import CandidateProfileModal from '../Components/tracking/CandidateProfileModal';
import { getDecryptedData, getUserType } from '../utils/authStorage';
import { getTrackingSummary } from '../services/trackingService';
import { formatDisplayDate } from '../utils/dateFormat';

function TrackingFlow({ stages = [], activeStageKey = '', onStageClick }) {
  return (
    <div className="hr-tracking-flow-wrap">
      <div className="hr-tracking-flow">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.key}>
            <button
              type="button"
              className={`hr-tracking-flow-stage${activeStageKey === stage.key ? ' is-active' : ''}`}
              onClick={() => onStageClick(stage)}
              style={{ '--stage-color': stage.color || '#2563eb' }}
            >
              <span className="hr-tracking-flow-icon">{stage.icon}</span>
              <span className="hr-tracking-flow-label">{stage.label}</span>
              <strong className="hr-tracking-flow-count">{stage.count}</strong>
            </button>
            {index < stages.length - 1 && <span className="hr-tracking-flow-arrow" aria-hidden>→</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function TrackingDashboard() {
  const userId = getDecryptedData('Id');
  const userType = getUserType();
  const isAdmin = userType === 'Admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [teamLeadId, setTeamLeadId] = useState('');
  const [recruiterId, setRecruiterId] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({});
  const [selectedStageKey, setSelectedStageKey] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [profileCandidateId, setProfileCandidateId] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);

  const loadSummary = useCallback(async (filters) => {
    if (!userId) return;
    setLoading(true);
    setLoadError('');
    try {
      const response = await getTrackingSummary({
        userId,
        ...filters,
      });
      setData(response);
    } catch (error) {
      console.error('Tracking summary load failed:', error);
      setLoadError(error.message || 'Failed to load tracking dashboard.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadSummary(appliedFilters);
  }, [loadSummary, appliedFilters]);

  const applyFilters = () => {
    setSelectedStageKey('');
    setAppliedFilters({
      from: fromDate,
      to: toDate,
      teamLeadId: isAdmin ? teamLeadId : '',
      recruiterId,
    });
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setTeamLeadId('');
    setRecruiterId('');
    setSelectedStageKey('');
    setAppliedFilters({});
  };

  const handleStageClick = (stage) => {
    setSelectedStageKey((current) => (current === stage.key ? '' : stage.key));
  };

  const openCandidateProfile = useCallback((row) => {
    if (!row?.candidateId) return;
    setProfileCandidateId(row.candidateId);
    setShowProfileModal(true);
  }, []);

  const closeCandidateProfile = () => {
    setShowProfileModal(false);
    setProfileCandidateId('');
  };

  const handleProfileUpdated = () => {
    loadSummary(appliedFilters);
  };

  const teamLeads = data?.filterOptions?.teamLeads || [];
  const recruiters = data?.filterOptions?.recruiters || [];
  const flowStages = data?.flowStages || [];
  const hasActiveFilters = !!(appliedFilters.from || appliedFilters.to || appliedFilters.teamLeadId || appliedFilters.recruiterId);

  const selectedStage = flowStages.find((stage) => stage.key === selectedStageKey) || null;

  const tableCandidates = useMemo(() => {
    const allCandidates = data?.candidates || [];
    if (!selectedStageKey) return allCandidates;
    return allCandidates.filter((row) => row.pipelineStageKey === selectedStageKey);
  }, [data?.candidates, selectedStageKey]);

  const columns = useMemo(() => [
    {
      key: 'candidateName',
      label: 'Source Profile',
      sortable: true,
      render: (row) => (
        <button type="button" className="btn btn-link p-0 hr-table-link-btn" onClick={(e) => { e.stopPropagation(); openCandidateProfile(row); }}>
          <strong>{row.candidateName}</strong>
        </button>
      ),
    },
    { key: 'pipelineStageLabel', label: 'Pipeline Stage', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    {
      key: 'regId',
      label: 'Req ID',
      sortable: true,
      render: (row) => (
        <button type="button" className="btn btn-link p-0 hr-table-link-btn" onClick={(e) => { e.stopPropagation(); openCandidateProfile(row); }}>
          {row.regId}
        </button>
      ),
    },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'recruiter', label: 'Recruiter', sortable: true },
    {
      key: 'currentStatus',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.currentStatus} useStoredLabel />,
    },
    {
      key: 'interviewDateLabel',
      label: 'Interview Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.interviewDateLabel || row.interviewDate),
    },
    { key: 'lastUpdatedLabel', label: 'Status Updated', sortable: true },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            openCandidateProfile(row);
          }}
        >
          View
        </button>
      ),
    },
  ], [openCandidateProfile]);

  return (
    <PageShell
      title="Status Tracking"
      subtitle="Pipeline overview — click a stage to filter, then click a profile row to view details and update status."
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Reports', path: '/reports' },
        { label: 'Status Tracking' },
      ]}
      backTo="/reports"
      actions={(
        <button type="button" className="hr-btn-secondary" onClick={() => loadSummary(appliedFilters)} disabled={loading}>
          Refresh
        </button>
      )}
    >
      <section className="hr-card hr-tracking-filters">
        <button
          type="button"
          className="hr-tracking-filters-toggle"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
        >
          <span>Advanced Filters{hasActiveFilters ? ' (active)' : ''}</span>
          <span aria-hidden>{filtersOpen ? '▲' : '▼'}</span>
        </button>
        {filtersOpen && (
          <>
            <div className="hr-tracking-filter-grid">
              <label>
                From
                <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label>
                To
                <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
              {isAdmin && (
                <label>
                  Team Lead
                  <select className="form-select" value={teamLeadId} onChange={(e) => setTeamLeadId(e.target.value)}>
                    <option value="">All Team Leads</option>
                    {teamLeads.map((tl) => (
                      <option key={tl.id} value={tl.id}>{tl.name}</option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Recruiter
                <select className="form-select" value={recruiterId} onChange={(e) => setRecruiterId(e.target.value)}>
                  <option value="">All Recruiters</option>
                  {recruiters.map((rec) => (
                    <option key={rec.id} value={rec.id}>{rec.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="hr-tracking-filter-actions">
              <button type="button" className="hr-btn-primary" onClick={applyFilters}>Apply Filters</button>
              <button type="button" className="hr-btn-secondary" onClick={clearFilters}>Clear</button>
            </div>
          </>
        )}
      </section>

      {loadError && <p className="text-danger">{loadError}</p>}
      {loading && !data && <p className="hr-page-hint">Loading tracking data...</p>}

      {data && (
        <>
          <p className="hr-page-hint">
            Showing {data.totalProfiles} profile{data.totalProfiles === 1 ? '' : 's'} in scope.
            {selectedStage
              ? ` Filtered to ${selectedStage.label} (${tableCandidates.length}).`
              : ' All pipeline stages are listed in the table below.'}
            {' '}Click a row, name, Req ID, or View to open profile details.
          </p>

          <section className="hr-card">
            <div className="hr-section-toolbar">
              <h3>Recruitment Pipeline</h3>
              {selectedStageKey && (
                <button type="button" className="hr-btn-secondary" onClick={() => setSelectedStageKey('')}>
                  Show All Stages
                </button>
              )}
            </div>
            <TrackingFlow
              stages={flowStages}
              activeStageKey={selectedStageKey}
              onStageClick={handleStageClick}
            />
          </section>

          <section className="hr-card">
            <h3>
              {selectedStage ? `${selectedStage.label} — Source Profiles` : 'All Source Profiles'}
            </h3>
            <DataTable
              columns={columns}
              data={tableCandidates}
              rowKey="candidateId"
              searchPlaceholder="Search candidates..."
              searchKeys={[
                'candidateName',
                'pipelineStageLabel',
                'currentStatus',
                'client',
                'regId',
                'role',
                'recruiter',
                'interviewDateLabel',
                'lastUpdatedLabel',
              ]}
              exportFileName="status-tracking-candidates.csv"
              loading={loading}
              emptyMessage={selectedStage ? `No source profiles in ${selectedStage.label}.` : 'No source profiles found.'}
              onRowClick={openCandidateProfile}
            />
          </section>

          <CandidateProfileModal
            show={showProfileModal}
            onHide={closeCandidateProfile}
            candidateId={profileCandidateId}
            onUpdated={handleProfileUpdated}
          />
        </>
      )}
    </PageShell>
  );
}

export default TrackingDashboard;
